import { useState, useEffect, useCallback } from 'react';

export interface Route {
  view: string;
  params: Record<string, string>;
}

/** Parse window.location.hash into a view name and params */
function parseHash(): Route {
  const hash = window.location.hash.slice(1) || '/';
  const [path, queryString] = hash.split('?');
  const segments = path.split('/').filter(Boolean);

  const params: Record<string, string> = {};
  if (queryString) {
    new URLSearchParams(queryString).forEach((v, k) => {
      params[k] = v;
    });
  }

  if (segments.length === 0) return { view: 'home', params };

  // /area/:areaId
  if (segments[0] === 'area' && segments[1]) {
    return { view: 'area', params: { ...params, areaId: segments[1] } };
  }

  // /exercise/:exerciseId
  if (segments[0] === 'exercise' && segments[1]) {
    return { view: 'exercise', params: { ...params, exerciseId: segments[1] } };
  }

  // /source/:sourceId?name=...
  if (segments[0] === 'source' && segments[1]) {
    return { view: 'source-study', params: { ...params, sourceId: segments[1] } };
  }

  // Simple routes: /study, /sequential, /lessons, /cards, /settings, /stats
  return { view: segments[0], params };
}

/** Build a hash string from a path and optional params */
export function buildHash(path: string, params?: Record<string, string>): string {
  const query = params ? '?' + new URLSearchParams(params).toString() : '';
  return `#${path}${query}`;
}

/** Hook that provides hash-based routing with back-button support */
export function useHashRouter() {
  const [route, setRoute] = useState<Route>(parseHash);

  useEffect(() => {
    const handler = () => setRoute(parseHash());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  const navigate = useCallback((path: string, params?: Record<string, string>) => {
    window.location.hash = buildHash(path, params).slice(1); // buildHash includes #
  }, []);

  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  return { route, navigate, goBack };
}
